/** @jsxImportSource @emotion/react */
import React, { useState } from "react";
import {
    FPFormRow, FPFormGroup, FPLabel, FPInput, FPSelect, FPTextarea,
    FPCheckRow, FPCheckGrid,
    FPMethodGrid, FPMethodName, FPMethodMeta, FPMethodBadge,
    FPCategoryHeader,
    FPAlertBanner, FPAlertIcon, FPAlertContent, FPAlertTitle, FPAlertDetail, FPAlertAction,
    FPActionBar, FPPrimaryBtn, FPSecondaryBtn,
    FPDisclosure, FPDisclosureToggle, FPDisclosureContent,
    FPEligibilityPanel, FPEligibilityHeader, FPEligibilityBody,
    FPEligibilityList, FPEligibilityLabel,
} from "./FamilyPlanningStyles";
import Form from "react-bootstrap/Form";
import Label from "../../../Utils/FormInputs/Label";
import SelectInput from "../../../Utils/FormInputs/SelectInput";
import TextField from "../../../Utils/FormInputs/TextField";
import { ResizableTextarea } from "../../../Utils/FormInputs/Textarea";


/* ═══════════════════════════════════════════════════════════════
   FamilyPlanningForm — FP Counselling & Service Form (Task 17)
   Sections: Client Info, Method Selection, Eligibility,
             Vitals Check, Side Effects, Counselling Notes
   ═══════════════════════════════════════════════════════════════ */

const alertIcons = { critical: "🚨", warning: "⚠️", info: "ℹ️", success: "✅" };

export const FamilyPlanningForm = function (props) {
    const fp = props.fp;
    const form = fp.form;
    const handleChange = fp.handleChange;

    /* ─── Local state for collapsible sections ─── */
    const sideEffectState = useState(false);
    const showSideEffects = sideEffectState[0];
    const setShowSideEffects = sideEffectState[1];

    const eligState = useState(false);
    const showEligPanel = eligState[0];
    const setShowEligPanel = eligState[1];

    /* ─── Get selected method info ─── */
    const selectedMethodInfo = fp.FP_METHODS.find(function (m) { return m.value === form.method_provided; });
    const selectedCategory = selectedMethodInfo
        ? fp.FP_METHOD_CATEGORIES.find(function (c) { return c.value === selectedMethodInfo.category; })
        : null;

    /* ─── Group methods by category ─── */
    const methodsByCategory = {};
    fp.FP_METHOD_CATEGORIES.forEach(function (cat) {
        methodsByCategory[cat.value] = fp.FP_METHODS.filter(function (m) { return m.category === cat.value; });
    });

    return (
        <div>
            {/* ─── BP / Eligibility Alerts ─── */}
            {fp.bpAlerts.map(function (alert, i) {
                return (
                    <FPAlertBanner key={"bp-" + i} type={alert.type}>
                        <FPAlertIcon>{alertIcons[alert.type]}</FPAlertIcon>
                        <FPAlertContent>
                            <FPAlertTitle>{alert.title}</FPAlertTitle>
                            <FPAlertDetail>{alert.detail}</FPAlertDetail>
                            <FPAlertAction>{alert.action}</FPAlertAction>
                        </FPAlertContent>
                    </FPAlertBanner>
                );
            })}

            {/* ═══ Section 1: Client Information ═══ */}
                    <FPFormRow cols="1fr 1fr 1fr">
                        <Form.Group>
                            <Label name="Client Type" />
                            <SelectInput name="client_type" value={form.client_type} onChange={handleChange} defaultOption="— Select —" options={fp.CLIENT_TYPES.map(function (t) {
                                return { value: t.value, text: t.label };
                            })} />

                            
                        </Form.Group>
                        <Form.Group>
                            <Label name="Provision Date" />
                            <TextField type="date" name="provision_date" value={form.provision_date} onChange={handleChange} />
                        </Form.Group>
                        <Form.Group>
                            <Label name="Status" />
                            <SelectInput name="status" value={form.status} onChange={handleChange} defaultOption="— Select —" options={fp.FP_STATUS_OPTIONS.map(function (s) {
                                return { value: s.value, text: s.label };
                            })} />
                        </Form.Group>
                    </FPFormRow>

                    {/* Show switched_from if restart or if status=switched */}
                    {(form.client_type === "restart" || form.status === "switched") && (
                        <FPFormRow>
                            <Form.Group>
                                <Label name="Switched From" />
                                <SelectInput name="switched_from" value={form.switched_from} onChange={handleChange} defaultOption="— Select previous method —" options={fp.FP_METHODS.map(function (m) {
                                    return { value: m.value, text: m.label };
                                })} />
                            </Form.Group>
                                    
                        
                            <Form.Group>
                                <Label name="Reason for Switch / Restart" />
                                <TextField type="text" name="switch_reason" value={form.switch_reason} onChange={handleChange}
                                    placeholder="e.g. Side effects, method failure" />
                            </Form.Group>
                        </FPFormRow>
                    )}

                    {form.status === "discontinued" && (
                        <FPFormRow cols="1fr">
                            <Form.Group>
                                <Label name="Reason for Discontinuation" />
                                <SelectInput
                                 name="discontinuation_reason" value={form.discontinuation_reason} onChange={handleChange}
                                    defaultOption="— Select reason —" options={fp.DISCONTINUATION_REASONS.map(function (r) {
                                        return { value: r.value, text: r.label };
                                    })} />
                                    
                            </Form.Group>
                        </FPFormRow>
                    )}

            {/* ═══ Section 2: Method Selection ═══ */}

                    <FPFormRow cols="1fr">
                        <Form.Group>
                            <Label name="Method Provided" />
                            <SelectInput name="method_provided" value={form.method_provided} onChange={function(e) { fp.selectMethod(e.target.value); }}
                                defaultOption="— Select method —" options={fp.FP_METHODS.map(function(m) {
                                    return { value: m.value, text: m.label };
                                })} />
                            </Form.Group>
                        </FPFormRow>
                    

                    {/* Quantity for pills/condoms */}
                    {selectedMethodInfo && (selectedMethodInfo.category === "short_acting" || selectedMethodInfo.category === "barrier") && (
                        <FPFormRow cols="1fr 1fr">
                            <Form.Group>
                                <Label name="Quantity Provided" />
                                <TextField type="number" name="quantity_provided" value={form.quantity_provided}
                                    onChange={handleChange} placeholder="e.g. 3 cycles, 30 condoms" min="0" />
                            </Form.Group>
                            <Form.Group>
                                <Label name="Expected Renewal Date" />
                                <TextField type="date" name="expected_renewal_date" value={form.expected_renewal_date}
                                    onChange={handleChange} />
                            </Form.Group>
                        </FPFormRow>
                    )}

                    {/* LARC renewal */}
                    {selectedMethodInfo && selectedMethodInfo.category === "larc" && form.expected_renewal_date && (
                        <FPFormRow cols="1fr">
                            <Form.Group>
                                <Label name="Auto-Calculated Renewal Date" />
                                <TextField type="date" name="expected_renewal_date" value={form.expected_renewal_date}
                                    onChange={handleChange} />
                            </Form.Group>
                        </FPFormRow>
                    )}

            {/* ═══ Section 3: Medical Eligibility ═══ */}
            {form.method_provided && (
                <>

                        {/* Contraindications noted */}
                        {fp.currentEligibility && fp.currentEligibility.contraindications.length > 0 && (
                            <div>
                                <Label name="Contraindications Identified (check any that apply)" />
                                <FPCheckGrid>
                                    {fp.currentEligibility.contraindications.map(function (ci) {
                                        const isChecked = (form.contraindications_noted || []).indexOf(ci) >= 0;
                                        return (
                                            <FPCheckRow key={ci} checked={isChecked}
                                                onClick={function () { fp.toggleContraindication(ci); }}>
                                                <input type="checkbox" checked={isChecked} readOnly
                                                    />
                                                {ci}
                                            </FPCheckRow>
                                        );
                                    })}
                                </FPCheckGrid>
                            </div>
                        )}


                        <div style={{ marginTop: "12px" }}>
                            <FPCheckRow
                                checked={form.medical_eligibility_checked}
                                onClick={function () {
                                    handleChange({
                                        target: {
                                            name: "medical_eligibility_checked",
                                            type: "checkbox",
                                            checked: !form.medical_eligibility_checked,
                                        },
                                    });
                                }}
                            >
                                <input type="checkbox" checked={form.medical_eligibility_checked} readOnly
                                    />
                                <strong>I confirm medical eligibility has been assessed *</strong>
                            </FPCheckRow>
                        </div>
                </>
            )}

            {/* ═══ Section 4: Vitals Check ═══ */}
            {selectedMethodInfo && selectedMethodInfo.requiresBP && (
                        <FPFormRow cols="1fr 1fr 1fr">
                            <Form.Group>
                                <Label name="BP Systolic (mmHg) *" />
                                <TextField type="number" name="bp_systolic" value={form.bp_systolic}
                                    onChange={handleChange} placeholder="e.g. 120" min="60" max="260" />
                            </Form.Group>
                            <Form.Group>
                                <Label name="BP Diastolic (mmHg) *" />
                                <TextField type="number" name="bp_diastolic" value={form.bp_diastolic}
                                    onChange={handleChange} placeholder="e.g. 80" min="30" max="160" />
                            </Form.Group>
                            <Form.Group>
                                <Label name="Weight (kg)" />
                                <TextField type="number" name="weight_check" value={form.weight_check}
                                    onChange={handleChange} placeholder="e.g. 65" min="20" max="300" step="0.1" />
                            </Form.Group>
                        </FPFormRow>
            )}

            {/* ═══ Section 5: Side Effects ═══ */}
            {(form.client_type === "revisit" || form.client_type === "restart") && (
                        <FPDisclosure>
                            <FPDisclosureToggle
                                open={showSideEffects}
                                onClick={function () { setShowSideEffects(!showSideEffects); }}
                            >
                                <span>
                                    {(form.side_effects || []).length > 0 && (form.side_effects || [])[0] !== "none"
                                        ? "Side effects reported (" + form.side_effects.length + ")"
                                        : "Record any side effects"
                                    }
                                </span>
                                <span>{showSideEffects ? "▲" : "▼"}</span>
                            </FPDisclosureToggle>
                            {showSideEffects && (
                                <FPDisclosureContent>
                                    <FPCheckGrid>
                                        {fp.SIDE_EFFECT_OPTIONS.map(function (se) {
                                            const isChecked = (form.side_effects || []).indexOf(se.value) >= 0;
                                            return (
                                                <FPCheckRow key={se.value} checked={isChecked}
                                                    onClick={function () { fp.toggleSideEffect(se.value); }}>
                                                    <input type="checkbox" checked={isChecked} readOnly />
                                                    {se.label}
                                                </FPCheckRow>
                                            );
                                        })}
                                    </FPCheckGrid>
                                    <Form.Group>
                                        <Label name="Side Effect Management" />
                                        <ResizableTextarea name="side_effect_management"
                                            value={form.side_effect_management}
                                            onChange={handleChange}
                                            placeholder="Document management of side effects..." />
                                    </Form.Group>
                                </FPDisclosureContent>
                            )}
                        </FPDisclosure>
            )}

            {/* ═══ Section 6: Counselling Notes ═══ */}
                    <Form.Group>
                        <Label name="Counselling Notes" />
                        <ResizableTextarea name="counselling_notes" value={form.counselling_notes}
                            onChange={handleChange}
                            placeholder="Document FP counselling provided, client's preferences, informed consent..."
                            style={{ minHeight: "100px" }} />
                    </Form.Group>

            {/* ═══ Action Bar ═══ */}
            <FPActionBar>
                {fp.editingRecord && (
                    <FPSecondaryBtn onClick={fp.resetForm}>
                        Cancel Edit
                    </FPSecondaryBtn>
                )}
                <button className="btn sms-btn " onClick={fp.saveFpRecord} disabled={fp.saving}>
                    {fp.saving
                        ? "Saving..."
                        : fp.editingRecord
                            ? "Update FP Record"
                            : "Save FP Record"
                    }
                </button>
            </FPActionBar>
        </div>
    );
};
